/**
 * IncomingTransferGroups - Groups incoming transfers by collection
 *
 * Replaces the flat transfer list with accordion-style collection groups.
 * Each group shows collection info, transfer count, and a bulk accept button.
 * Unknown/unrecognized NFTs are grouped at the bottom without bulk accept.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Accordion } from '../../ui/components/Accordion';
import { Table } from '../../ui/components/Table';
import { Avatar } from '../../ui/components/Avatar';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { FeatherChevronDown } from '@subframe/core';
import { BulkAcceptModal } from './BulkAcceptModal';
import type { WalletOffer } from '../../types/wallet';

interface IncomingTransferGroupsProps {
  offers: WalletOffer[];
  isOwnWallet: boolean;
  processing: boolean;
  currentOfferId: string | null;
  onAcceptTransfer: (offerId: string, nftTokenId: string) => void;
  onRejectTransfer: (offerId: string, nftTokenId: string) => void;
  onBlockAddress: (address: string) => void;
  blockingAddress: string | null;
  onOfferRemoved: (offerId: string) => void;
  onCacheInvalidate: () => void;
}

export interface TransferCollectionGroup {
  key: string; // issuer:taxon or "unknown"
  collectionName: string | null;
  collectionImage: string | null;
  collectionSlug: string | null;
  isWhitelisted: boolean;
  senders: string[];
  offers: WalletOffer[];
}

function groupTransfersByCollection(offers: WalletOffer[]): TransferCollectionGroup[] {
  const groups = new Map<string, TransferCollectionGroup>();

  for (const offer of offers) {
    const key =
      offer.issuer && offer.taxon != null
        ? `${offer.issuer}:${offer.taxon}`
        : 'unknown';

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        collectionName: offer.collectionName,
        collectionImage: offer.collectionImage,
        collectionSlug: offer.collectionSlug,
        isWhitelisted: offer.isWhitelisted ?? false,
        senders: [],
        offers: [],
      });
    }

    const group = groups.get(key)!;
    group.offers.push(offer);
    if (!group.senders.includes(offer.owner)) {
      group.senders.push(offer.owner);
    }
  }

  // Sort: whitelisted first, then known collections by count desc, then unknown last
  const sorted = Array.from(groups.values()).sort((a, b) => {
    if (a.key === 'unknown') return 1;
    if (b.key === 'unknown') return -1;
    if (a.isWhitelisted !== b.isWhitelisted) return a.isWhitelisted ? -1 : 1;
    return b.offers.length - a.offers.length;
  });

  return sorted;
}

function formatAddress(address: string): string {
  return `${address.slice(0, 8)}...${address.slice(-6)}`;
}

export const IncomingTransferGroups: React.FC<IncomingTransferGroupsProps> = ({
  offers,
  isOwnWallet,
  processing,
  currentOfferId,
  onAcceptTransfer,
  onRejectTransfer,
  onBlockAddress,
  blockingAddress,
  onOfferRemoved,
  onCacheInvalidate,
}) => {
  const groups = useMemo(() => groupTransfersByCollection(offers), [offers]);
  const [bulkAcceptGroup, setBulkAcceptGroup] = useState<TransferCollectionGroup | null>(null);

  const handleBulkAcceptComplete = useCallback(() => {
    onCacheInvalidate();
  }, [onCacheInvalidate]);

  if (offers.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex items-center justify-between w-full">
        <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
          Incoming Transfers
        </span>
        <span className="text-body font-body text-subtext-color">
          {offers.length} transfer{offers.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="flex w-full flex-col gap-3">
        {groups.map((group) => (
          <CollectionTransferGroup
            key={group.key}
            group={group}
            isOwnWallet={isOwnWallet}
            processing={processing}
            currentOfferId={currentOfferId}
            onAcceptTransfer={onAcceptTransfer}
            onRejectTransfer={onRejectTransfer}
            onBlockAddress={onBlockAddress}
            blockingAddress={blockingAddress}
            onBulkAccept={() => setBulkAcceptGroup(group)}
          />
        ))}
      </div>

      {/* Bulk Accept Modal */}
      {bulkAcceptGroup && (
        <BulkAcceptModal
          open={!!bulkAcceptGroup}
          onClose={() => setBulkAcceptGroup(null)}
          offers={bulkAcceptGroup.offers}
          collectionName={bulkAcceptGroup.collectionName || 'Unknown Collection'}
          collectionImage={bulkAcceptGroup.collectionImage}
          onOfferAccepted={onOfferRemoved}
          onComplete={handleBulkAcceptComplete}
        />
      )}
    </div>
  );
};

// ---------- Collection Group ----------

interface CollectionTransferGroupProps {
  group: TransferCollectionGroup;
  isOwnWallet: boolean;
  processing: boolean;
  currentOfferId: string | null;
  onAcceptTransfer: (offerId: string, nftTokenId: string) => void;
  onRejectTransfer: (offerId: string, nftTokenId: string) => void;
  onBlockAddress: (address: string) => void;
  blockingAddress: string | null;
  onBulkAccept: () => void;
}

const CollectionTransferGroup: React.FC<CollectionTransferGroupProps> = ({
  group,
  isOwnWallet,
  processing,
  currentOfferId,
  onAcceptTransfer,
  onRejectTransfer,
  onBlockAddress,
  blockingAddress,
  onBulkAccept,
}) => {
  const isUnknown = group.key === 'unknown';
  const defaultOpen = group.offers.length <= 10;
  const senderText =
    group.senders.length === 1
      ? `from ${formatAddress(group.senders[0])}`
      : `from ${group.senders.length} wallets`;

  const collectionHref = !isUnknown && group.collectionSlug
    ? `/collections/${group.collectionSlug}`
    : null;

  const triggerContent = (
    <div className="flex items-center justify-between w-full py-3">
      <div className="flex items-center gap-3">
        {/* Collection image — linked if collection page exists */}
        {collectionHref ? (
          <a href={collectionHref} onClick={(e) => e.stopPropagation()} className="hover:opacity-80">
            <Avatar size="medium" image={group.collectionImage || undefined} square>
              {group.collectionName?.[0] || 'C'}
            </Avatar>
          </a>
        ) : (
          <Avatar size="medium" image={group.collectionImage || undefined} square>
            {isUnknown ? '?' : (group.collectionName?.[0] || 'C')}
          </Avatar>
        )}

        {/* Collection info */}
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            {collectionHref ? (
              <a
                href={collectionHref}
                onClick={(e) => e.stopPropagation()}
                className="text-body-bold font-body-bold text-default-font hover:text-brand-700 hover:underline"
              >
                {group.collectionName || 'Unnamed Collection'}
              </a>
            ) : (
              <span className="text-body-bold font-body-bold text-default-font">
                {isUnknown ? 'Unknown Collections' : (group.collectionName || 'Unnamed Collection')}
              </span>
            )}
            {group.isWhitelisted && (
              <Badge variant="success">Verified</Badge>
            )}
            {isUnknown && (
              <Badge variant="warning">Unrecognized</Badge>
            )}
          </div>
          <span className="text-caption font-caption text-subtext-color">
            {group.offers.length} transfer{group.offers.length !== 1 ? 's' : ''} {senderText}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Bulk accept button (not for unknown collections) */}
        {isOwnWallet && !isUnknown && group.offers.length > 1 && (
          <Button
            variant="brand-primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onBulkAccept();
            }}
          >
            Accept All ({group.offers.length})
          </Button>
        )}
        <Accordion.Chevron />
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <Accordion defaultOpen={defaultOpen} trigger={triggerContent}>
        <div className="w-full overflow-x-auto pl-4 mobile:pl-2">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>NFT</Table.HeaderCell>
                <Table.HeaderCell>From</Table.HeaderCell>
                {isOwnWallet && <Table.HeaderCell>Action</Table.HeaderCell>}
              </Table.HeaderRow>
            }
          >
            {group.offers.map((offer) => (
              <Table.Row key={offer.offerId} className="h-16">
                <Table.Cell className="h-16">
                  <a
                    href={`/nfts/${offer.nftTokenId}`}
                    className="flex items-center gap-3 hover:opacity-80"
                  >
                    <Avatar
                      size="medium"
                      image={offer.nftImage || undefined}
                      square
                    >
                      N
                    </Avatar>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {offer.nftName || 'Unnamed NFT'}
                    </span>
                  </a>
                </Table.Cell>
                <Table.Cell className="h-16">
                  <a
                    href={`/wallets/${offer.owner}`}
                    className="text-body font-body text-brand-700 hover:text-brand-800 hover:underline"
                    title={offer.owner}
                  >
                    {formatAddress(offer.owner)}
                  </a>
                </Table.Cell>
                {isOwnWallet && (
                  <Table.Cell className="h-16">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="brand-primary"
                        size="small"
                        onClick={() => onAcceptTransfer(offer.offerId, offer.nftTokenId)}
                        disabled={processing && currentOfferId === offer.offerId}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="neutral-secondary"
                        size="small"
                        onClick={() => onRejectTransfer(offer.offerId, offer.nftTokenId)}
                        disabled={processing && currentOfferId === offer.offerId}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="destructive-secondary"
                        size="small"
                        onClick={() => onBlockAddress(offer.owner)}
                        disabled={blockingAddress === offer.owner}
                      >
                        Block
                      </Button>
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table>
        </div>
      </Accordion>
    </div>
  );
};
